import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthPaidMediaTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinDatePipe } from '../../../shared/design-system/ain-date.pipe';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { defaultCalendarFromUtc, defaultCalendarToUtc } from '../growth-datetime.util';
import { GrowthCampaign, GrowthMarketingService } from '../growth-marketing.service';
import {
  GrowthPaidMediaCampaignLinkDto,
  GrowthPaidMediaRoasDto,
  GrowthPaidMediaSpendSnapshotDto,
  campaignLinkDisplayLabel
} from './growth-paid-media.models';
import { GrowthPaidMediaService } from './growth-paid-media.service';

@Component({
  standalone: true,
  selector: 'ain-growth-paid-media-roas',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    JalaliDateInputComponent,
    FormsModule,
    ButtonModule,
    SelectModule,
    TableModule,
    RouterLink,
    AinDatePipe
  ],
  template: `
    <ain-page [title]="t('growth.paidMedia.roas.title')" [description]="t('growth.paidMedia.roas.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.paidMedia.roas.help.title'"
        [descriptionKey]="'growth.paidMedia.roas.help.description'"
        [bulletKeys]="['growth.paidMedia.roas.help.bullets.pull', 'growth.paidMedia.roas.help.bullets.live']" />

      <section class="card">
        <div class="toolbar">
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
          <a class="link-btn" routerLink="/growth/paid-media/campaign-links">{{ t('growth.paidMedia.roas.openCampaignLinks') }}</a>
        </div>
        <div class="grid">
          <ain-form-field [label]="t('growth.paidMedia.roas.fields.campaignLink')">
            <p-select
              [options]="campaignLinkOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="form.campaignLinkId"
              [filter]="true"
              appendTo="body"
              [placeholder]="t('growth.paidMedia.roas.fields.campaignLinkPlaceholder')" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.paidMedia.roas.fields.periodStart')">
            <ain-date-input [(ngModel)]="form.periodStartUtc" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.paidMedia.roas.fields.periodEnd')">
            <ain-date-input [(ngModel)]="form.periodEndUtc" />
          </ain-form-field>
        </div>
        <div class="toolbar">
          <button
            pButton
            type="button"
            severity="secondary"
            [label]="t('growth.paidMedia.roas.actions.pullSpend')"
            [loading]="busy() === 'spend'"
            (click)="pullSpend()"></button>
          <button
            pButton
            type="button"
            [label]="t('growth.paidMedia.roas.actions.refreshRoas')"
            [loading]="busy() === 'roas'"
            (click)="refreshRoas()"></button>
        </div>
      </section>

      <section class="card">
        <h3>{{ t('growth.paidMedia.roas.sections.roas') }}</h3>
        @if (!roas()) {
          <ain-state type="empty" [title]="t('growth.paidMedia.roas.emptyRoas')" />
        } @else {
          <div class="metrics">
            <div class="metric">
              <span class="label">{{ t('growth.paidMedia.roas.fields.spend') }}</span>
              <strong>{{ roas()!.spend }}</strong>
            </div>
            <div class="metric">
              <span class="label">{{ t('growth.paidMedia.roas.fields.revenue') }}</span>
              <strong>{{ roas()!.revenue }}</strong>
            </div>
            <div class="metric">
              <span class="label">{{ t('growth.paidMedia.roas.fields.roas') }}</span>
              <strong>{{ roas()!.roas }}</strong>
            </div>
          </div>
        }
      </section>

      <section class="card">
        <h3>{{ t('growth.paidMedia.roas.sections.spend') }}</h3>
        @if (spendRows().length === 0) {
          <ain-state type="empty" [title]="t('growth.paidMedia.roas.emptySpend')" />
        } @else {
          <p-table [value]="spendRows()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.paidMedia.roas.columns.periodStart') }}</th>
                <th>{{ t('growth.paidMedia.roas.columns.periodEnd') }}</th>
                <th>{{ t('growth.paidMedia.roas.columns.impressions') }}</th>
                <th>{{ t('growth.paidMedia.roas.columns.clicks') }}</th>
                <th>{{ t('growth.paidMedia.roas.columns.cost') }}</th>
                <th>{{ t('growth.paidMedia.roas.columns.conversions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.periodStartUtc | ainDate:'datetime' }}</td>
                <td>{{ row.periodEndUtc | ainDate:'datetime' }}</td>
                <td>{{ row.impressions }}</td>
                <td>{{ row.clicks }}</td>
                <td>{{ row.cost }}</td>
                <td>{{ row.conversions }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3 { margin: 0; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
    .metric { border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; display: grid; gap: 6px; }
    .metric .label { color: #64748b; font-size: 12px; font-weight: 600; }
    .metric strong { font-size: 1.35rem; color: #0f172a; }
    .link-btn { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .message.error { color: #b91c1c; }
    @media (max-width: 960px) {
      .grid, .metrics { grid-template-columns: 1fr; }
    }
  `]
})
export class GrowthPaidMediaRoasComponent implements OnInit {
  private readonly api = inject(GrowthPaidMediaService);
  private readonly marketing = inject(GrowthMarketingService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly links = signal<GrowthPaidMediaCampaignLinkDto[]>([]);
  readonly campaigns = signal<GrowthCampaign[]>([]);
  readonly spendRows = signal<GrowthPaidMediaSpendSnapshotDto[]>([]);
  readonly roas = signal<GrowthPaidMediaRoasDto | null>(null);
  readonly busy = signal<'spend' | 'roas' | null>(null);
  readonly errorMessage = signal('');

  form = {
    campaignLinkId: null as string | null,
    periodStartUtc: defaultCalendarFromUtc(),
    periodEndUtc: defaultCalendarToUtc()
  };

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.form.campaignLinkId = this.route.snapshot.queryParamMap.get('campaignLinkId');

    this.liveUpdateService.events$
      .pipe(
        filter(event => {
          if (event.eventType !== LiveUpdateEventTypes.growthPaidMediaChanged) {
            return false;
          }
          const payloadId = event.payload['campaignLinkId'];
          return !this.form.campaignLinkId
            || payloadId === undefined
            || payloadId === null
            || String(payloadId).toLowerCase() === this.form.campaignLinkId.toLowerCase();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.form.campaignLinkId) {
          this.refreshRoas(false);
        }
      });

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.loadLookups();
  }

  campaignLinkOptions(): Array<{ label: string; value: string }> {
    return this.links().map(link => {
      const campaignName = this.campaigns().find(item => item.id === link.growthCampaignId)?.name ?? null;
      return {
        value: link.id,
        label: campaignLinkDisplayLabel(link, campaignName)
      };
    });
  }

  loadLookups(): void {
    this.api.listCampaignLinks().subscribe({
      next: rows => {
        this.links.set(rows);
        if (!this.form.campaignLinkId) {
          this.form.campaignLinkId = rows[0]?.id ?? null;
        }
      },
      error: () => this.links.set([])
    });
    this.marketing.listCampaigns().subscribe({
      next: rows => this.campaigns.set(rows),
      error: () => this.campaigns.set([])
    });
  }

  pullSpend(): void {
    if (!this.validateForm()) {
      return;
    }
    this.busy.set('spend');
    this.errorMessage.set('');
    this.api.pullSpend(this.form.campaignLinkId!, this.form.periodStartUtc, this.form.periodEndUtc)
      .pipe(finalize(() => this.busy.set(null)))
      .subscribe({
        next: rows => this.spendRows.set(rows),
        error: () => this.errorMessage.set(this.t('growth.paidMedia.roas.messages.spendFailed'))
      });
  }

  refreshRoas(showBusy = true): void {
    if (!this.validateForm()) {
      return;
    }
    if (showBusy) {
      this.busy.set('roas');
    }
    this.errorMessage.set('');
    this.api.getRoas(this.form.campaignLinkId!, this.form.periodStartUtc, this.form.periodEndUtc)
      .pipe(finalize(() => {
        if (showBusy) {
          this.busy.set(null);
        }
      }))
      .subscribe({
        next: row => this.roas.set(row),
        error: () => this.errorMessage.set(this.t('growth.paidMedia.roas.messages.roasFailed'))
      });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private validateForm(): boolean {
    if (!this.form.campaignLinkId || !this.form.periodStartUtc || !this.form.periodEndUtc) {
      this.errorMessage.set(this.t('growth.paidMedia.roas.messages.validationFailed'));
      return false;
    }
    return true;
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthPaidMediaTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
